import Head from 'next/head'
import Link from 'next/link'
import { getWordpressPostList } from '@src/functions/wordpress'
import { getPostSummaries, serializePost, PostSummary } from '@src/functions/blog'
import classes from '@src/styles/blogIndex.module.css'
import WordpressPostSummary from '../common/components/WordpressPostSummary'

const SITE = 'https://getdimension.com'

/**
 * Built at deploy time, not per request. See the note in [post].tsx: Firebase
 * does not upload `src/`, so reading markdown at runtime finds nothing in
 * production. Building the list here bakes it into `.next`, which does ship.
 */
export async function getStaticProps() {
  const [posts, wordpress] = await Promise.all([
    getPostSummaries(),
    getWordpressPostList('1382').catch(() => []),
  ])

  // WordPress posts that have already been migrated to markdown would otherwise
  // appear twice in the list.
  const migrated = new Set(posts.map((post) => post.slug))
  const legacy = Array.isArray(wordpress)
    ? wordpress.filter((post: any) => !migrated.has(post?.slug))
    : []

  // No `revalidate` — see [post].tsx. Regenerating this in production would
  // rebuild the list without src/content and silently drop every local post.
  // The archive list therefore refreshes on deploy, not on a timer.
  return { props: { posts: serializePost(posts), legacy } }
}

function formatDate(iso: string) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

function Blog({ posts, legacy }: { posts: PostSummary[]; legacy: any[] }) {
  return (
    <>
      <Head>
        <title>Waste Management Blog | Dimension</title>
        <meta
          name="description"
          content="Practical guidance on waste disposal, recycling and compliance for businesses — costs, regulations, and how to handle specific waste streams."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href={`${SITE}/blog`} />
      </Head>

      <div className={classes.root}>
        <header className={classes.header}>
          <h1 className={classes.title}>Dimension Blog</h1>
          <p className={classes.subtitle}>
            Practical guidance on waste disposal, recycling and compliance for businesses.
          </p>
        </header>

        {posts.length > 0 && (
          <ul className={classes.grid}>
            {posts.map((post) => (
              <li key={post.slug} className={classes.card}>
                <Link href={`/blog/${post.slug}`} className={classes.cardLink}>
                  {post.category && <span className={classes.category}>{post.category}</span>}
                  <h2 className={classes.cardTitle}>{post.title}</h2>
                  <p className={classes.cardDescription}>{post.description}</p>
                  <p className={classes.cardMeta}>
                    <time dateTime={post.date}>{formatDate(post.date)}</time>
                    <span aria-hidden="true"> · </span>
                    {post.readingMinutes} min read
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {legacy.length > 0 && (
          <section className={classes.legacy}>
            <h2 className={classes.legacyHeading}>Archive</h2>
            {legacy.map((post: any) => (
              <WordpressPostSummary group="blog" post={post} key={post?.id ?? post?.slug} />
            ))}
          </section>
        )}
      </div>
    </>
  )
}

export default Blog
